import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {LoadingButton} from '@mui/lab';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import {LocationOnRounded} from '@mui/icons-material';
import {DialogCloseButton} from './DialogCloseButton';
import api from '../../../../services/api';
import toast from "react-hot-toast";
import {TimeField} from '@mui/x-date-pickers/TimeField';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';


interface OptionType {
  id: number | string;
  name: string;
}

interface GuideOptionType {
  id: number | string;
  name: string;
  phone?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: {
    groupName?: string;
    headCount?: string | number;
    date?: string;
    startTime?: string;
    selectedItem?: OptionType | null;
    selectedGuide?: GuideOptionType | null;
    guideName?: string;
    guidePhone?: string;
    address?: string;
  };
  mode?: 'create' | 'edit';
  reservationId?: number | string | null;
}

interface FormState {
  groupName: string;
  headCount: string;
  date: string;
  startTime: string;
  selectedItem: OptionType | null;
  selectedGuide: GuideOptionType | null;
  guideName: string;
  guidePhone: string;
  address: string;
}

type FieldErrorKey =
  | 'groupName'
  | 'headCount'
  | 'date'
  | 'startTime'
  | 'selectedItem'
  | 'guideName';

type FieldErrors = Partial<Record<FieldErrorKey, string>>;
const getTodayDate = () => dayjs().format('YYYY-MM-DD');

const initialForm: FormState = {
  groupName: '',
  headCount: '',
  date: getTodayDate(),
  startTime: '',
  selectedItem: null,
  selectedGuide: null,
  guideName: '',
  guidePhone: '',
  address: '',
};

const DialogPaper = React.forwardRef<HTMLDivElement, any>(function DialogPaper(props, ref) {
  return (
    <Paper
      ref={ref}
      {...props}
      elevation={0}
      sx={{
        borderRadius: {xs: 0, sm: 4},
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)',
        boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
        ...props.sx,
      }}
    />
  );
});

export const CreateReservationDialog: React.FC<Props> = ({
                                                           open,
                                                           onClose,
                                                           onSuccess,
                                                           initialData,
                                                           mode = 'create',
                                                           reservationId = null,
                                                         }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<FormState>(initialForm);
  const [saving, setSaving] = useState(false);

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const [guideAutocompleteOpen, setGuideAutocompleteOpen] = useState(false);
  const [guideOptions, setGuideOptions] = useState<GuideOptionType[]>([]);
  const [loadingGuideOptions, setLoadingGuideOptions] = useState(false);
  const [guideSearch, setGuideSearch] = useState('');

  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const groupNameRef = useRef<HTMLInputElement | null>(null);
  const headCountRef = useRef<HTMLInputElement | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);
  const startTimeRef = useRef<HTMLInputElement | null>(null);
  const programRef = useRef<HTMLInputElement | null>(null);
  const guideNameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;

    setForm({
      groupName: String(initialData?.groupName ?? ''),
      headCount: initialData?.headCount ? String(initialData.headCount) : '',
      date: String(initialData?.date ?? getTodayDate()),
      startTime: String(initialData?.startTime ?? ''),
      selectedItem: initialData?.selectedItem ?? null,
      selectedGuide: initialData?.selectedGuide ?? null,
      guideName: String(initialData?.guideName ?? ''),
      guidePhone: String(initialData?.guidePhone ?? ''),
      address: String(initialData?.address ?? ''),
    });

    setError('');
    setFieldErrors({});
  }, [open, initialData]);

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setError('');
    setFieldErrors({});
    setAutocompleteOpen(false);
    setGuideAutocompleteOpen(false);
    setGuideSearch('');
  }, []);

  const handleChange = useCallback((field: keyof FormState, value: any) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;

      const next = {...prev};
      delete next[field as FieldErrorKey];
      return next;
    });
  }, []);

  const fetchOptions = useCallback(async () => {
    if (optionsLoaded || loadingOptions) return;

    try {
      setLoadingOptions(true);

      const response: any = await api.get('/service-provider/reservation/activities');

      const items = Array.isArray(response)
        ? response
        : response?.data || response?.items || [];

      const mapped: OptionType[] = items.map((item: any) => ({
        id: item.id,
        name: item.name || item.title || item.label || '',
      }));

      setOptions(mapped);
      setOptionsLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingOptions(false);
    }
  }, [optionsLoaded, loadingOptions]);

  const handleAutocompleteOpen = useCallback(async () => {
    setAutocompleteOpen(true);
    await fetchOptions();
  }, [fetchOptions]);

  const fetchGuideOptions = useCallback(async (search: string) => {
    try {
      setLoadingGuideOptions(true);

      const searchParams = new URLSearchParams({
        search,
      });

      const response: any = await api.get(
        '/service-provider/external-guides/search',
        searchParams
      );

      const items = Array.isArray(response)
        ? response
        : response?.data || response?.items || [];

      const mapped: GuideOptionType[] = items.map((item: any) => ({
        id: item.id,
        name: item.name || '',
        phone: item.phone || '',
      }));

      setGuideOptions(mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingGuideOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!guideAutocompleteOpen) return;

    const timer = setTimeout(() => {
      fetchGuideOptions(guideSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [guideSearch, guideAutocompleteOpen, fetchGuideOptions]);

  useEffect(() => {
    if (!form.selectedGuide) return;

    setGuideOptions((prev: GuideOptionType[]) => {
      const exists = prev.some(
        (item) => String(item.id) === String(form.selectedGuide?.id)
      );

      if (exists) {
        return prev;
      }

      return [form.selectedGuide as GuideOptionType, ...prev];
    });
  }, [form.selectedGuide]);

  const validateForm = useCallback(() => {
    const errors: FieldErrors = {};

    if (!form.groupName.trim()) {
      errors.groupName = 'A csoport neve kötelező.';
    }

    if (!form.headCount || Number(form.headCount) <= 0) {
      errors.headCount = 'A létszám kötelező, és legalább 1 főnek kell lennie.';
    }

    if (!form.date) {
      errors.date = 'A dátum megadása kötelező.';
    }

    if (!form.startTime) {
      errors.startTime = 'A kezdési időpont megadása kötelező.';
    }

    if (!form.selectedItem) {
      errors.selectedItem = 'A program kiválasztása kötelező.';
    }

    if (form.guidePhone.trim() && !form.guideName.trim()) {
      errors.guideName = 'Ha guide telefonszámot adsz meg, a guide neve is szükséges.';
    }

    return errors;
  }, [form]);

  const focusFirstInvalidField = useCallback((errors: FieldErrors) => {
    const fieldRefMap: Partial<Record<FieldErrorKey, React.RefObject<HTMLInputElement | null>>> = {
      groupName: groupNameRef,
      headCount: headCountRef,
      date: dateRef,
      startTime: startTimeRef,
      selectedItem: programRef,
      guideName: guideNameRef,
    };

    const firstErrorKey = Object.keys(errors)[0] as FieldErrorKey | undefined;

    if (!firstErrorKey) return;

    const element = fieldRefMap[firstErrorKey]?.current;

    if (!element) return;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    setTimeout(() => {
      element.focus();
    }, 250);
  }, []);

  const handleClose = useCallback(() => {
    if (saving) return;

    resetForm();
    onClose();
  }, [saving, resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    const errors = validateForm();

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors);

      setError(`Kérlek javítsd a hibás mezőket: ${errorMessages.join(' ')}`);
      focusFirstInvalidField(errors);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        group_name: form.groupName.trim(),
        size: Number(form.headCount),
        date: form.date,
        start_time: form.startTime,
        activity_id: form.selectedItem?.id,
        external_guide_id: form.selectedGuide?.id || null,
        guide_name: form.guideName.trim() || null,
        guide_phone: form.guidePhone.trim() || null,
        address: form.address.trim() || null,
      };

      if (mode === 'edit') {
        if (!reservationId) {
          throw new Error('Hiányzó reservationId szerkesztéshez.');
        }

        await api.patch(`/service-provider/reservations/${reservationId}`, payload);
      } else {
        await api.post('/service-provider/reservations', payload);
      }

      resetForm();
      onClose();
      onSuccess?.();
    } catch (e) {
      console.error(e);
      setError(
        mode === 'edit'
          ? 'A foglalás módosítása sikertelen volt.'
          : 'A foglalás létrehozása sikertelen volt.'
      );
    } finally {
      setSaving(false);
    }
  }, [
    form,
    mode,
    reservationId,
    onClose,
    onSuccess,
    resetForm,
    validateForm,
    focusFirstInvalidField,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      keepMounted
      PaperComponent={DialogPaper}
    >
      <Box sx={{position: 'relative'}}>
        <DialogCloseButton onClick={handleClose}/>

        <DialogTitle
          sx={{
            pt: 3,
            pb: 1.5,
            px: {xs: 2, sm: 3},
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  boxShadow: '0 10px 24px rgba(25, 118, 210, 0.25)',
                  flexShrink: 0,
                }}
              >
                <EventRoundedIcon/>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  {mode === 'edit' ? 'Foglalás szerkesztése' : 'Új foglalás létrehozása'}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{mt: 0.3}}
                >
                  Add meg a foglalás alapadatait.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </DialogTitle>

        <Divider/>

        <DialogContent sx={{px: {xs: 2, sm: 3}, py: 3}}>
          <Stack gap="10px">
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  inputRef={groupNameRef}
                  label="Csoport neve"
                  placeholder="Pl. Tavaszi utazó csoport"
                  value={form.groupName}
                  onChange={(e) => handleChange('groupName', e.target.value)}
                  error={!!fieldErrors.groupName}
                  helperText={fieldErrors.groupName}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GroupRoundedIcon sx={{color: 'text.secondary'}}/>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  inputRef={headCountRef}
                  label="Létszám"
                  type="number"
                  value={form.headCount}
                  onChange={(e) => handleChange('headCount', e.target.value)}
                  error={!!fieldErrors.headCount}
                  helperText={fieldErrors.headCount}
                  inputProps={{min: 1}}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleAltRoundedIcon sx={{color: 'text.secondary'}}/>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Dátum"
                    value={form.date ? dayjs(form.date) : null}
                    onChange={(value: Dayjs | null) => {
                      handleChange('date', value ? value.format('YYYY-MM-DD') : '');
                    }}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        inputRef: dateRef,
                        error: !!fieldErrors.date,
                        helperText: fieldErrors.date,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthRoundedIcon sx={{color: 'text.secondary'}}/>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimeField
                    fullWidth
                    label="Kezdés"
                    format="HH:mm"
                    value={form.startTime ? dayjs(form.startTime, 'HH:mm') : null}
                    onChange={(newValue) => {
                      const formattedTime = newValue && newValue.isValid()
                        ? newValue.format('HH:mm')
                        : '';

                      toast('Time: ' + formattedTime);

                      handleChange('startTime', formattedTime);
                    }}
                    slotProps={{
                      textField: {
                        inputRef: startTimeRef,
                        error: !!fieldErrors.startTime,
                        helperText: fieldErrors.startTime,
                        InputLabelProps: {shrink: true},
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeRoundedIcon sx={{color: 'text.secondary'}}/>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  disabled={mode === 'edit'}
                  open={autocompleteOpen}
                  onOpen={handleAutocompleteOpen}
                  onClose={() => setAutocompleteOpen(false)}
                  options={options}
                  loading={loadingOptions}
                  value={form.selectedItem}
                  onChange={(_, newValue) => handleChange('selectedItem', newValue)}
                  isOptionEqualToValue={(option, value) =>
                    String(option.id) === String(value.id)
                  }
                  getOptionLabel={(option) => option?.name || ''}
                  noOptionsText={loadingOptions ? 'Betöltés...' : 'Nincs találat'}
                  loadingText="Betöltés..."
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{py: 0.5}}
                      >
                        <BusinessRoundedIcon fontSize="small" color="action"/>
                        <Typography variant="body2">{option.name}</Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={programRef}
                      label="Program"
                      placeholder="Kattints a listához"
                      error={!!fieldErrors.selectedItem}
                      helperText={fieldErrors.selectedItem}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <BusinessRoundedIcon sx={{color: 'text.secondary'}}/>
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingOptions ? (
                              <CircularProgress color="inherit" size={18}/>
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Helyszín (opcionális)"
                  placeholder="Pl. Hotel recepció, Zürich"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnRounded sx={{color: 'text.secondary'}}/>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  open={guideAutocompleteOpen}
                  onOpen={() => setGuideAutocompleteOpen(true)}
                  onClose={() => setGuideAutocompleteOpen(false)}
                  options={guideOptions}
                  loading={loadingGuideOptions}
                  value={form.selectedGuide}
                  inputValue={form.guideName}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      handleChange('guideName', newInputValue);
                      handleChange('selectedGuide', null);
                      setGuideSearch(newInputValue);
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                      handleChange('guideName', newValue);
                      handleChange('selectedGuide', null);
                      return;
                    }

                    if (!newValue) {
                      handleChange('selectedGuide', null);
                      handleChange('guideName', '');
                      handleChange('guidePhone', '');
                      return;
                    }

                    handleChange('selectedGuide', newValue);
                    handleChange('guideName', newValue.name || '');
                    handleChange('guidePhone', newValue.phone || '');
                  }}
                  isOptionEqualToValue={(option, value) =>
                    String(option.id) === String(value.id)
                  }
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.name || '';
                  }}
                  filterOptions={(x) => x}
                  noOptionsText={loadingGuideOptions ? 'Betöltés...' : 'Nincs találat'}
                  loadingText="Betöltés..."
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Stack spacing={0.25} sx={{py: 0.5}}>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.phone ? (
                          <Typography variant="caption" color="text.secondary">
                            {option.phone}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={guideNameRef}
                      label="Guide neve (opcionális)"
                      placeholder="Kezdd el beírni a guide nevét"
                      error={!!fieldErrors.guideName}
                      helperText={fieldErrors.guideName}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PersonRoundedIcon sx={{color: 'text.secondary'}}/>
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingGuideOptions ? (
                              <CircularProgress color="inherit" size={18}/>
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Guide telefonszám (opcionális)"
                  value={form.guidePhone}
                  onChange={(e) => handleChange('guidePhone', e.target.value)}
                  placeholder="+36..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneRoundedIcon sx={{color: 'text.secondary'}}/>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{pt: 1}}
            >
              <Button
                onClick={handleClose}
                disabled={saving}
                color="inherit"
                variant="text"
                sx={{
                  minWidth: 96,
                  fontWeight: 600,
                }}
              >
                Mégse
              </Button>

              <LoadingButton
                onClick={handleSubmit}
                loading={saving}
                variant="contained"
                sx={{
                  minWidth: 140,
                  borderRadius: 2.5,
                  boxShadow: '0 10px 24px rgba(25, 118, 210, 0.25)',
                }}
              >
                {mode === 'edit' ? 'Mentés' : 'Hozzáadás'}
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogContent>
      </Box>
    </Dialog>
  );
};